import { useTranslation } from 'react-i18next';
import { stockRowAction } from '@d-orca/shared/client/logistics';
import type { InventoryLevel } from '@interfaces/ILogistics';
import { arrivalLateDays, etaLate, formatDay, zonedDay } from '../logistics.format';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import { incomingLine } from './incomingLine';
import { LineOrButton, TruckGlyph, JumpChevron, InlineJumpText } from './InventoryRowGlyphs';
import { InventoryRowMain, InventoryRowGauge } from './InventoryRowCore';
import { rowClickHandler } from './rowActivation';
import styles from './InventoryLevels.module.css';

/** The row actions a tracked material row raises — shared by the panel
 *  (`InventoryLevels`), which threads them through unchanged. */
export interface IInventoryRowActions {
  /** Reconcile the stock against reality: count what is left, add what arrived,
   *  close a cycle on the difference. The ONLY stock-closing action — a row that
   *  has run out is this with nothing left, prefilled 0. */
  onSync: (level: InventoryLevel) => void;
  /** Renew, having confirmed the in-flight delivery HAS landed: the order moves
   *  to delivered and its quantity seeds the new stock. Split from `onRenew`
   *  because only the user knows — a request nobody advanced past "awaiting"
   *  may have arrived days ago or never. */
  onRenewArrived: (level: InventoryLevel) => void;
  /** Row-tap on the red "Order now" line — opens a NEW delivery order prefilled
   *  with this material (spec §3 step 3). Never touches the stock dialog. */
  onOrderNow: (level: InventoryLevel) => void;
  /** A line that NAMES a delivery offers to go to it. The panel does not own
   *  the pipeline, so it raises the ref and the page routes it. */
  onJumpToDelivery: (deliveryRef: string) => void;
  /** Material currently being deleted — its row fades under a "Deleting…" overlay. */
  deletingMaterial: string | null;
}

interface IInventoryRowProps extends IInventoryRowActions {
  level: InventoryLevel;
  /** How much of THIS material the incoming order carries — its LINE's
   *  amount, resolved by the panel (`incomingAmountFor`). Null when there is
   *  no incoming order or it recorded no amount for this material. */
  incomingAmount: string | null;
  /** The PROJECT's IANA timezone — the calendar every day-level reading here
   *  is measured against. */
  tz: string;
  /** The row's one affordance — open the material's detail drawer (edit and
   *  delete moved there off the kebab, the same move the delivery cards
   *  made). The row's inline actions (Sync, Order now…) keep their own
   *  handlers and never trigger this. */
  onOpenDetail: (level: InventoryLevel) => void;
  /** Arrival prompts already answered "not yet" for today (see
   *  `useInventoryDueDismissed`) — keyed the same way as `dueKey`. */
  dueDismissed: ReadonlySet<string>;
  /** Keys an arrival prompt to this material, its in-flight order, and today —
   *  so a dismissal is scoped to the one ask it answered. */
  dueKey: (level: InventoryLevel) => string;
  /** Settles the day's arrival prompt for this material without opening any
   *  dialog. */
  dismissDue: (level: InventoryLevel) => void;
  /** The caller's role may write. Without it Sync / Order now / Renew and
   *  Dismiss are not rendered (Dismiss is a per-user note, but it answers the
   *  arrival ask, which is not a viewer's call) and the overdue line stays
   *  text. Jumps to a delivery stay live. */
  canEdit: boolean;
}

/**
 * One tracked material's row: real self-reported stock (snapshot + estimate).
 * Arrives derived and urgency-sorted from the backend; this component only
 * renders. Honesty rules (spec §3): every figure is labeled an estimate, and
 * an exhausted estimate reads "needs checking" — never "0d left".
 */
export function InventoryRow({
  level,
  incomingAmount,
  tz,
  onOpenDetail,
  onSync,
  onRenewArrived,
  onOrderNow,
  onJumpToDelivery,
  deletingMaterial,
  dueDismissed,
  dueKey,
  dismissDue,
  canEdit,
}: IInventoryRowProps) {
  const { t, i18n } = useTranslation();
  const { locked } = useLogisticsEditLock();

  /** The two honest answers to "did it arrive?" — Sync (records the arrival on
   *  save) and Dismiss (settles the ask for the day). Shared by the ran-out row
   *  and the covered/in-flight row so the pair can never drift apart. */
  const dueAnswers = (level: InventoryLevel) =>
    canEdit ? (
      <>
        <button
          type="button"
          className={styles.action}
          onClick={() => onRenewArrived(level)}
          disabled={locked}
        >
          {t('logistics.inventory.dueSync')}
        </button>
        <button type="button" className={styles.action} onClick={() => dismissDue(level)} disabled={locked}>
          {t('logistics.inventory.dueDismiss')}
        </button>
      </>
    ) : null;

  /** The onClick + accessible label a line uses to jump to a delivery — a
   *  button when there is a ref to go to, inert otherwise. One place for the
   *  ref-gated pair every jump site would otherwise spell out by hand. */
  const jumpProps = (ref: string | null) =>
    ref
      ? { onClick: () => onJumpToDelivery(ref), label: t('logistics.inventory.jumpToDelivery', { ref }) }
      : { onClick: undefined, label: undefined };

  const deleting = level.materialName === deletingMaterial;
  // Computed once (used for the class and the copy below). Capturing
  // the due date in a local lets the "late" branch narrow it to a
  // string, so the arrived-overdue line needs no non-null assertion.
  const arrivedLate = arrivalLateDays(level, tz);
  const arrivalDueDate = level.lastArrivalDueDate;
  // The one action this row offers (shared with the mobile sheet): the chain
  // below only renders it, so the two surfaces cannot drift apart.
  const action = stockRowAction(level, { dueDismissed: dueDismissed.has(dueKey(level)) });
  const dueAsk = action.kind === 'dueSync';
  /* The ran-out line's action — null when the viewer's role has none. It
     depends on what, if anything, is coming. A due order asks "arrived?"; a
     landed one is a Sync (record it); a row that ran out with NOTHING on the
     way is prompted to reorder — recounting an empty shelf is not the next
     move, ordering more is. */
  const ranOutAction = dueAsk ? (
    /* A question with two answers. "Sync" opens the sync
       dialog; the arrival is recorded only when that dialog
       is saved, never on the click itself. "Later" settles
       the prompt right here and silences it until tomorrow —
       the ask returns each new day the order stays overdue
       (see `useInventoryDueDismissed`). */
    <>
      {/* The ask jumps to the in-flight order it is about
          (`incomingRef` — always set when a due order is
          unconfirmed). */}
      <InlineJumpText className={styles.arrivalNote} {...jumpProps(level.incomingRef)}>
        {t('logistics.inventory.dueAsk')}
      </InlineJumpText>
      {dueAnswers(level)}
    </>
  ) : action.kind === 'draft' || action.kind === 'order' ? (
    /* Ran out and nothing is on the way (no arrival to
       reconcile, nothing in flight) — the next move is to
       reorder, not recount. But if a draft was ALREADY raised
       for this material, point at it instead of spawning a
       second one — the same rule the overdue line follows,
       "'Order now' raising a SECOND draft is not what anyone
       means by that". Sync stays below for the cases where a
       delivery actually arrived or is due. */
    action.kind === 'draft' ? (
      <button
        type="button"
        className={styles.action}
        onClick={() => onJumpToDelivery(action.draftRef)}
      >
        {t('logistics.inventory.reorderDraft', { ref: action.draftRef })}
      </button>
    ) : canEdit ? (
      <button
        type="button"
        className={styles.action}
        onClick={() => onOrderNow(level)}
        disabled={locked}
      >
        {t('logistics.inventory.reorder')}
      </button>
    ) : null
  ) : action.kind === 'none' ? (
    /* Ran out while a replacement is on its way and not yet due
       (ruling 2026-09-15): the honest state is "waiting on that
       order", in the same words the covered row uses — with the
       jump to it, and NO Sync. A bare "Sync" here read as "sign
       for it" when nothing had arrived; the prompts that make
       Sync meaningful come back on their own — "Order arrived?"
       once the ETA passes, "new delivery arrived" once it lands. */
    <InlineJumpText className={styles.arrivalNote} {...jumpProps(level.incomingRef)}>
      {incomingLine(level, tz, i18n.language, t)}
      {incomingAmount !== null ? ` · ${incomingAmount}` : null}
    </InlineJumpText>
  ) : canEdit ? (
    /* A landed delivery waiting to be recorded: Sync is the commit. */
    <button
      type="button"
      className={styles.action}
      onClick={() => onSync(level)}
      disabled={locked}
    >
      {t('logistics.inventory.sync')}
    </button>
  ) : null;
  return (
    <li
      className={styles.row}
      /* A click anywhere on the row opens its record — a pointer convenience.
         Its inline actions are real <button>s scattered across shared glyph
         components, and a click on any of them is theirs, not the row's. */
      onClick={rowClickHandler(() => onOpenDetail(level), deleting)}
      data-deleting={deleting || undefined}
      // A ran-out row renders no tank (ReorderTickTank bails on needsCheck), so
      // its badge has nothing to centre against — pin it top-right like the
      // untracked row's, rather than floating it in the vertical middle.
      data-needscheck={level.needsCheck || undefined}
    >
      {deleting ? (
        <span className={styles.deletingOverlay} role="status">
          {t('logistics.inventory.deleting')}
        </span>
      ) : null}
      {/* The row's keyboard and screen-reader action: a real button, named by
          the material, so the list item is never a button wrapping buttons.
          Hidden; its focus ring is drawn on the row. */}
      <button
        type="button"
        className={`visually-hidden ${styles.rowOpen}`}
        aria-label={level.materialName}
        onClick={() => onOpenDetail(level)}
        disabled={deleting}
      />
      <div className={styles.main}>
        {/* Name, meta line(s), and — for a tracked (non-needsCheck) row —
            the started-date/provenance/lead line. Shared with the SiteMap
            AreaDrawer's stock rows via `InventoryRowCore`; a
            needsCheck row's OWN interactive provenance line (ran-out
            copy + Sync/Order actions) stays here, below, since that part
            is unique to this surface. */}
        <InventoryRowMain level={level} tz={tz} />
        {level.needsCheck ? (
          <span className={styles.provenance}>
            {t('logistics.inventory.ranOut')}
            {/* A landed delivery is WHY the answer to "did it run
                  out?" is usually yes, and until now the row knew that
                  and said nothing — needsCheck wins the label chain,
                  so the arrival was invisible unless you opened the
                  pipeline. It stays informational: Renew is the action
                  either way, this just stops the row withholding the
                  one fact that decides it. */}{' '}
            {/* Bold: this answers the question the line is asking,
                  sitting in the row's quietest text. Weight, not
                  colour — the tone is already spoken for by the row's
                  alert state.

                  Silence is not an answer: a blank line reads the
                  same whether the material was handled or forgotten,
                  which is the one thing a ran-out row must make
                  obvious.

                  Gated on `deliveryIncoming` — a row that ran out
                  while a replacement is in flight must not be told
                  nothing was ordered. That row still says nothing. */}
            {level.awaitingRenewal || !level.deliveryIncoming ? (
              <>
                {' '}
                {level.awaitingRenewal && level.lastArrivalAt ? (
                  /* The named arrival jumps to its delivery when the order is
                   known (it always is here — the arrival came FROM an order). */
                  <InlineJumpText className={styles.arrivalNote} {...jumpProps(level.lastArrivalRef)}>
                    {t('logistics.inventory.arrivedOn', {
                      d: formatDay(zonedDay(level.lastArrivalAt, tz), i18n.language),
                    })}
                  </InlineJumpText>
                ) : (
                  /* Nothing was ordered — no delivery to point at. */
                  <span className={styles.arrivalNote}>{t('logistics.inventory.noDelivery')}</span>
                )}
                {/* No action for this viewer: no separator left dangling. */}
                {ranOutAction ? ' ·' : null}
              </>
            ) : null}
            {ranOutAction}
          </span>
        ) : null}
        {/* Ordered chain, first match wins — renewal beats arrival
            beats the order prompt (spec §4). needsCheck rows never
            reach this chain — their Renew is already on the
            provenance line above, so showing it again here would
            just be the same prompt twice. */}
        {level.needsCheck ? null : action.kind === 'sync' ? (
          /* A delivery landed on stock that still has cover, so both
             amounts are on site and one count has to become both.
             That is Sync, not Renew: nothing ran out, and closing a
             full-batch cycle here would claim it had.

             It STATES the arrival rather than asking "arrived?" — that
             is settled once the card is in Delivered. But a delivery
             that landed LATE keeps its overdue note here: the amber
             "arrived Nd overdue" the in-flight row showed does not
             vanish just because the goods turned up, and it keeps the
             Estimated/Was-due distinction (a supplier-confirmed due
             date vs our projection). */
          <span className={arrivedLate > 0 ? `${styles.renewAsk} ${styles.renewAskLate}` : styles.renewAsk}>
            {/* The line NAMES a delivery, so it goes to it — the same jump the
                ran-out row's arrival note already offered off the same
                `lastArrivalRef`. Only the phrase is the target: Sync sits on
                this line too, and a whole-line button would swallow it. */}
            <InlineJumpText className={styles.renewAskJump} {...jumpProps(level.lastArrivalRef)}>
              {arrivedLate > 0 && arrivalDueDate != null
                ? t(
                    level.lastArrivalDueExact
                      ? 'logistics.inventory.arrivedOverdueOn'
                      : 'logistics.inventory.arrivedOverdueAbout',
                    {
                      n: arrivedLate,
                      date: formatDay(arrivalDueDate, i18n.language),
                    },
                  )
                : t('logistics.inventory.syncAsk')}
            </InlineJumpText>
            {canEdit ? (
              <button type="button" className={styles.action} onClick={() => onSync(level)} disabled={locked}>
                {t('logistics.inventory.sync')}
              </button>
            ) : null}
          </span>
        ) : level.deliveryIncoming ? (
          /* An in-flight delivery shows its ETA note AND — while it is
             not yet in Delivered — a "did it arrive?" ask beneath it.
             The two are NOT exclusive: an overdue order still owes both
             the amber "Nd overdue" note and the question, since it is
             not delivered and is only likelier to have landed. The ask
             falls away once the card reaches Delivered — that path is
             `awaitingRenewal` above, which STATES the arrival rather
             than asking about it. */
          <>
            {/* An ETA that has already elapsed is not an arrival time.
             "Arriving 7/27" on the 6th of August reads as nonsense and
             quietly tells the row it is covered when nothing has shown
             up. Past its own date the line reports the shortfall
             instead, and drops the green: a delivery nobody has seen
             is not reassurance. */}
            <LineOrButton
              className={styles.hint}
              tone={etaLate(level, tz) > 0 ? 'late' : 'incoming'}
              {...jumpProps(level.incomingRef)}
            >
              <TruckGlyph />
              {/* The same words the ran-out line uses for the same truck —
                  see `incomingLine`. */}
              {incomingLine(level, tz, i18n.language, t)}
              {/* How much of THIS material is on that truck. The separator is
                  structural punctuation, not copy — the board card's meta line
                  uses the same literal " · ". */}
              {incomingAmount !== null ? ` · ${incomingAmount}` : null}
              {level.incomingRef ? <JumpChevron /> : null}
            </LineOrButton>
            {/* Still in-flight (not Delivered) and its due date has come:
              the order is owed the arrival question, sitting under its
              overdue note. Dismiss silences only the ask — the note
              stays — and re-asks the next day it is still out (dueKey
              carries the day). */}
            {dueAsk ? (
              <span className={styles.renewAsk}>
                {t('logistics.inventory.dueAsk')}
                {dueAnswers(level)}
              </span>
            ) : null}
          </>
        ) : 'leadTimeDays' in action ? (
          /* Tappable (spec §3 step 3): past the reorder point the line
             opens a delivery order prefilled with this material. It is
             a button, not a span — its click is its own, never the
             row's open-the-record click (see `openDetail`'s guard).
             ONE target, one meaning, chosen by whether a draft already
             exists: with one, the line goes to it, because "Order now"
             raising a SECOND draft is not what anyone means by that. */
          /* Amber on the tick day, red once the gap is real. Both say "order
             now", but they are not the same urgency: today an order still
             lands in time, and red is what the row has left to say tomorrow,
             when it no longer does. */
          <LineOrButton
            className={styles.hint}
            tone={action.leadTimeDays > level.daysLeft ? 'gap' : 'reorder'}
            /* Without the role (or while the edit lock holds) the line stays as
               text — `LineOrButton` renders a span with no handler, so nothing
               looks clickable that isn't. The jump to an existing draft is a
               read and stays a button. */
            onClick={
              action.kind === 'draft'
                ? () => onJumpToDelivery(action.draftRef)
                : canEdit && !locked
                  ? () => onOrderNow(level)
                  : undefined
            }
            label={
              action.kind === 'draft'
                ? t('logistics.inventory.jumpToDelivery', { ref: action.draftRef })
                : undefined
            }
          >
            <TruckGlyph />
            {/* A draft raised through this very button does not stand
                the row down — nobody has been contacted — but saying
                nothing about it made the prompt look broken: you
                order, and the identical line comes back. It now names
                what is missing instead of repeating itself. */}
            {/* On the tick day itself the gap is 0, and "arrives ~0d after you
                run out" is both nonsense and the wrong urgency: nothing is late
                yet — today is simply the last day an order still lands in
                time. That day gets its own line; from tomorrow the gap is real
                and gets counted. */}
            {action.kind === 'draft'
              ? t('logistics.inventory.overdueDraft', { ref: action.draftRef })
              : action.leadTimeDays > level.daysLeft
                ? t('logistics.inventory.overdueHint', {
                    gap: action.leadTimeDays - level.daysLeft,
                  })
                : t('logistics.inventory.overdueHintToday')}
            {action.kind === 'draft' ? <JumpChevron /> : null}
          </LineOrButton>
        ) : null}
      </div>
      {/* Badge/cap + tank — the row's gauge cluster, shared with the SiteMap
          AreaDrawer's stock rows via `InventoryRowCore`. */}
      <InventoryRowGauge level={level} tz={tz} />
    </li>
  );
}
